/**
 * The PARENT-AUCTION precondition on an operator-chosen LANDING target
 * (BUG-1424, matrix §3.8 + invariant I4a; widened to the sold path by BUG-1470;
 * widened again by BUG-1506, matrix §7p, so a landing into the bidding rung
 * re-runs the parent auction's go-live gate — see that branch below).
 *
 * `canApplyLotTransition` decides the LOT-PHASE axis and is deliberately a pure
 * function of the lot's own phase — it has no auction input (BL-1044). That is
 * correct for every transition whose destination is fixed, because the auction
 * half of the rule can live on the transition definition. `reopen` is the one
 * transition whose destination is chosen by the operator at call time, so its
 * auction precondition is a function of the TARGET, not of the transition — and
 * had nowhere to live until this module.
 *
 * Without it the guard allowed `target: 'pre-bidding'` regardless of what the
 * auction could support: the lot's `pre_bidding_at` was stamped as asked, the
 * two-stamp `pre-bidding` rung in `deriveLotPhase` never fired (it needs
 * `auctions.pre_bidding_at` too), and the lot fell through to `ready` — the
 * operator picked one destination and silently got another.
 *
 * **Why this is landing-specific rather than shared with the two open doors.**
 * `open-for-bidding` (the door) requires a parent that is already `active`,
 * because opening a lot never changes auction status. An un-terminal landing
 * DOES change it — it un-closes a Closed auction in the same transaction
 * (BUG-1226) — so the question here is not "is the auction active now" but "can
 * the auction support this landing once the un-close has run". Those differ for
 * `closed` and for a finished-but-unstamped sale (BL-1516's
 * `isAuctionAwaitingClose`, which is what the retired `post-sale` phase used to
 * name here), which is why this cannot reuse `requireLiveParent`.
 *
 * **Why it is not reopen-specific (BUG-1470).** It shipped named for `reopen`
 * because that was its only caller. It never was reopen's rule: it is a function
 * of the TARGET and the parent auction, and `reverse-sale` lands in the same four
 * non-terminal targets (`REVERSE_SALE_TARGETS`' non-terminal tier is a type alias
 * of `LotLandingTarget`) through the same un-close. The sold path went ungated for
 * six weeks because the status dropdown routes a sold lot's rows to Reverse Sale
 * before they reach the reopen branch (matrix M4) — so the one surface that
 * consulted this rule was the one surface a sold lot never reached. The name is
 * now keyed to what the rule is about rather than to who called it first.
 */
import type { AuctionPhaseKind, LotPhaseKind } from './phase-tokens';
import type { BatchBlockReason } from './can-batch-apply';
import { isLotPhaseTerminal } from './lot-phase-predicates';

/**
 * The auction phases that carry `opened_at` — i.e. the auction's bidding window
 * has been opened at some point, so a lot stamped `bidding_opened_at` derives
 * `open-for-bidding` rather than falling through to `ready`.
 *
 * `closed` is in the set because `reopen` un-closes the auction in its own
 * transaction and a non-terminal lot then re-derives it as `active`. (BL-1516:
 * `post-sale` was a fifth member; it folded into `active`, already here.)
 * `paused` is in the set because `deriveLotPhase` reads the LOT's `paused_at`,
 * not the auction's — a lot reopened under a paused auction genuinely lands
 * `open-for-bidding`, so refusing it here would invent a block the matrix does
 * not state.
 */
const OPENED_AUCTION_PHASES: ReadonlySet<AuctionPhaseKind> = new Set<AuctionPhaseKind>([
  'active',
  'paused',
  'closed',
]);

/**
 * The operator-facing sentence for each reopen-target block, without its
 * terminal period.
 *
 * Declared beside the rule that produces the reason so the SERVER refusal and
 * the greyed dropdown row read identically — the operator who clicks through the
 * modal's own target picker and the operator who reads the disabled row are told
 * the same next action, in the same words. The wording for
 * `auction-past-pre-bidding` is specified verbatim by invariant I4a.
 */
export const LANDING_TARGET_BLOCK_MESSAGE: Record<
  | 'auction-past-pre-bidding'
  | 'auction-not-opened'
  | 'auction-not-go-live-ready'
  | 'auction-deleted'
  | 'auction-close-stage-set',
  string
> = {
  'auction-past-pre-bidding': 'Move the auction back to Accepting pre-bids first',
  'auction-not-opened': 'Open the auction for bidding first',
  // BUG-1506 — one sentence for a gate that can fail on several requirements
  // (a past close, a close before the start, missing terms). The SERVER refusal
  // additionally prefers the gate's own per-blocker sentences, the same
  // `preferDetail` shape BL-1559 gave `mark-ready` / `open`; this generic line
  // is what the greyed dropdown row renders, where no detail channel exists.
  // Names the fix rather than the refusal (rule #38 §6), and both remedies live
  // one level up on the AUCTION, which is where it points.
  'auction-not-go-live-ready': "Correct the auction's schedule and terms first",
  // BUG-1728 — `restore`; BUG-1729 widened it to every landing (reopen and
  // reverse-sale too). Names the auction as the blocker and the fix one level
  // up, per rule #38 §6; the auction is restored from its archived page.
  'auction-deleted': 'The auction was deleted — restore the auction first',
  // BL-2280 (PRJ-89 D7, matrix §7al) — the lot twin of the auction's
  // `close-stage-set` refusal: a live landing would un-close a staged auction,
  // and the one fix is the stage move back to stage 0. BL-2282 (plan §9) —
  // worded without naming the phase: a lot surface has no house label to hand.
  'auction-close-stage-set': 'Move the auction off its closing stage first',
};

/**
 * Decide whether a lot may be landed INTO `target` given its parent auction's
 * derived phase.
 *
 * Consulted by every surface that offers or performs an un-terminal landing, so
 * they cannot drift: both server gates (`reopenEligibilityTx` and
 * `reverseSaleEligibilityTx`), the `restore` transition's own parent gate
 * (BUG-1460), the batch check that greys the status-dropdown rows for each verb,
 * the dropdown's rendered reason, and both modals' target pickers.
 *
 * @param input.parentPhase The parent auction's ADR-023 derived phase kind.
 * @param input.target The landing phase the operator picked (`LOT_LANDING_TARGETS`).
 * @param input.auctionGoLiveBlockerCount How many go-live requirements the
 *   PARENT auction currently fails (`getAuctionReadyBlockers(...).length`).
 *   REQUIRED with no default, deliberately: a default of `0` would let a caller
 *   that simply forgot the input silently skip the BUG-1506 gate and reopen
 *   bidding on a stale auction — the exact failure this rule exists to stop. tsc
 *   makes every call site decide instead.
 *
 *   Passed IN rather than computed here for the same reason
 *   `canApplyAuctionTransition` takes `readyBlockerCount`: `getAuctionReadyBlockers`
 *   lives in `@auctogon/core-admin`, which depends on this package, so importing
 *   it would invert the dependency. Callers in the app layer already hold the
 *   auction row the gate reads.
 * @param input.parentAuctionAwaitingClose Whether the parent auction's sale has
 *   FINISHED but `closed_at` is not yet stamped ({@link isAuctionAwaitingClose}).
 *   REQUIRED with no default, for the same reason as the blocker count: this is
 *   half of the BUG-1506 gate, and BL-1516 moved it off a phase token onto a
 *   boolean, so a caller that forgot it would silently un-gate the door.
 * @param input.parentAuctionHasOpened Whether the parent's `opened_at` is set
 *   (BL-2278). REQUIRED with no default: since BL-2278 an auction can close from
 *   a pre-open phase, so `closed` no longer implies an opened parent, and a
 *   caller that forgot it would land an open-for-bidding lot that silently
 *   derives `ready`.
 * @param input.parentAuctionCloseStage The parent's derived post-close stage
 *   (`deriveCloseStage`, BL-2280). REQUIRED with no default: above 0 every
 *   non-terminal landing is refused, because its un-close (BUG-1226) would drop
 *   the auction out of its settlement stage; a caller that forgot it would
 *   reopen that side door.
 * @returns `null` when the landing is reachable, otherwise the shared
 *   {@link BatchBlockReason} naming the auction as the blocker —
 *   `'auction-deleted'` for any landing under a soft-deleted parent (BUG-1729).
 * @sideEffect none — pure.
 */
export function parentAuctionBlockForLandingTarget(input: {
  parentPhase: AuctionPhaseKind;
  target: LotPhaseKind;
  auctionGoLiveBlockerCount: number;
  parentAuctionAwaitingClose: boolean;
  parentAuctionHasOpened: boolean;
  parentAuctionCloseStage: number;
}): BatchBlockReason | null {
  const {
    parentPhase,
    target,
    auctionGoLiveBlockerCount,
    parentAuctionAwaitingClose,
    parentAuctionHasOpened,
    parentAuctionCloseStage,
  } = input;
  // BUG-1729 — first, and for EVERY target: a deleted parent cannot hold a live
  // lot (ADR 018 — Soft Delete's restrict model). The per-target rules below
  // cannot see it — `draft` and `ready` read no auction column, so they answered
  // null and a reopen put the lot back to work under an auction no surface can
  // reach. The delete guard's row lock (BUG-1729) stops new lots landing under
  // a deleted auction; this stops a lot already stranded there being reopened.
  // The same reason and copy as the restore landing (BUG-1728), not a fork.
  if (parentPhase === 'deleted') return 'auction-deleted';

  // BL-2280 (PRJ-89 D7, matrix §7al) — a staged parent takes no live lot. Every
  // non-terminal landing un-closes a Closed auction in its own transaction
  // (BUG-1226), which at stage > 0 would silently drop it out of its settlement
  // stage. Ahead of the per-target rules below: whatever else is wrong, the one
  // move that helps is back to plain Closed. The TERMINAL reverse-sale targets
  // (`unsold`, `withdrawn`) leave the auction closed and its stage intact, so
  // they stay open (AC 4). Explicit `> 0`: stage 0 is today (AC 5).
  if (parentAuctionCloseStage > 0 && !isLotPhaseTerminal({ kind: target })) {
    return 'auction-close-stage-set';
  }

  // Invariant I4a — `pre-bidding` is a two-stamp phase, and a LANDING can only
  // rely on the auction half while the auction is `accepting-pre-bids`. Landing
  // anywhere else writes the lot stamp as asked and leaves the lot deriving
  // something the operator did not pick; the reopen's un-close does not help,
  // because it clears `closed_at` and leaves `opened_at` set. The recovery is
  // one level up, on the AUCTION, which is what the copy points at.
  //
  // BUG-1460 (matrix §7v) — this is a POLICY, not a physics claim, and the
  // difference is worth stating because this comment used to make the stronger
  // one ("no lot-level stamp can produce the phase from any other auction
  // phase"). It can: `deriveLotPhase`'s rung reads the raw
  // `auctions.pre_bidding_at`, which `statusDecisionStampSet` PRESERVES through
  // an opened phase, so a lot the open sweep did not enrol goes on deriving
  // `pre-bidding` inside an Active auction — by design (I4 leaves it for an
  // operator, and the lot genuinely is taking advance max bids only). What this
  // rule forbids is CREATING a new one, which is a rule about transitions.
  //
  // BUG-1470: identical on the reverse-sale path. `reverseSaleCore` dispatches a
  // non-terminal target to the SAME `applyLotDecisionStampSet`, so the landing it
  // writes is silent for exactly the same reason. BUG-1460 added `restore` as the
  // third caller, on the same reasoning.
  if (target === 'pre-bidding') {
    return parentPhase === 'accepting-pre-bids' ? null : 'auction-past-pre-bidding';
  }

  // The mirror on the bidding rung: `open-for-bidding` needs `auctions.opened_at`.
  // An auction that has never opened (`draft` / `ready` / `accepting-pre-bids`)
  // leaves the lot deriving `ready`, which is the same silent landing one rung
  // over.
  if (target === 'open-for-bidding') {
    // BL-2278: the phase alone no longer answers "has it opened?" — a `closed`
    // parent may have been closed from Draft / Upcoming / Accepting pre-bids.
    // Its un-close (BUG-1226) would leave it pre-open and this lot deriving
    // `ready`, so it gets the same refusal a pre-open parent does.
    if (!OPENED_AUCTION_PHASES.has(parentPhase) || !parentAuctionHasOpened) {
      return 'auction-not-opened';
    }

    // BUG-1506 (matrix §7p) — the second precondition, and the one that closes
    // the last ungated door into a bid-accepting phase.
    //
    // Under a parent that is `closed`, or whose sale has FINISHED but is not
    // yet stamped closed, this landing does not merely place the lot: it makes
    // `lots_open_count_denormalized` non-zero, so the auction goes back to
    // accepting bids. `auctions.opened_at` survived the original run, so no
    // auction transition happens and the go-live gate — which
    // `mark-ready`, `open`, `open-pre-bidding` and restore→ready all consult —
    // is never reached. Reproduced end-to-end: a Closed auction whose close was
    // four months past went straight back to Accepting bids on one lot reopen.
    //
    // The gate is re-run in FULL, not just its past-close blocker: the invariant
    // worth holding is "an auction cannot reach a bid-accepting phase without
    // passing the go-live gate, by ANY route", and gating on a subset is how
    // this door came to be found the hard way in the first place.
    //
    // NOT applied to a RUNNING `active` / `paused` auction. Those already
    // cleared `open`'s gate, and an Active auction's `first_lot_close_at` is
    // legitimately in the past the moment its first lot closes — gating there
    // would refuse ordinary mid-sale lot reopening. Same trap §7o avoided by
    // narrowing `mark-pre-bidding`'s sources instead of gating the cell.
    //
    // BL-1516 (matrix §7w): the second half of this test used to read
    // `parentPhase === 'post-sale'`. That phase is retired, and a finished-but-
    // unstamped auction now derives plain `active` — so testing the phase alone
    // would fold the gate's two halves together and re-open BUG-1506. The
    // predicate the phase stood for is threaded in explicitly instead, which is
    // why `parentAuctionAwaitingClose` is a required input rather than a default.
    if (
      (parentPhase === 'closed' || parentAuctionAwaitingClose) &&
      auctionGoLiveBlockerCount > 0
    ) {
      return 'auction-not-go-live-ready';
    }
    return null;
  }

  // `draft` and `ready` are single-stamp prep phases — they read no auction
  // column, so every LIVE auction phase can support them (a deleted one was
  // refused above).
  return null;
}

/**
 * Invariant I2 — a lot may not be dropped back into cataloguing (`ready`)
 * beneath a sale that is already taking bids (matrix §3.2).
 *
 * Lifted here from `stamp-set-transitions.ts` (BUG-1685) so the CHECK that greys
 * a status-dropdown row and the EFFECT that performs the write consult one
 * function rather than two copies — the same discipline `buildReopenCheck`
 * already keeps for {@link parentAuctionBlockForLandingTarget}. `mark-ready` and
 * `restore` are its two callers; before the lift only the write path could see
 * it, which is exactly how a check and a server gate drift.
 *
 * @param input.parentPhase The parent auction's ADR-023 derived phase kind.
 * @param input.target The landing the operator picked.
 * @param input.parentAuctionAwaitingClose Whether the parent's sale has FINISHED
 *   but `closed_at` is not yet stamped (BL-1516, matrix §7w) — such an auction is
 *   no longer taking bids, so the rule does not apply to it even though it
 *   derives `active`.
 * @returns `null` when the landing is reachable, otherwise `'not-openable'`.
 * @sideEffect none — pure.
 */
export function parentAuctionBlockForReadyLanding(input: {
  parentPhase: AuctionPhaseKind;
  target: LotPhaseKind | 'deleted';
  parentAuctionAwaitingClose: boolean;
}): BatchBlockReason | null {
  const { parentPhase, target, parentAuctionAwaitingClose } = input;
  if (target !== 'ready') return null;
  // BL-1516 (matrix §7w): the rule is about a sale that is TAKING BIDS. An
  // auction whose lots have all gone terminal is not, and under the old ladder it
  // derived `post-sale` — a phase this rule never listed — so dropping a lot back
  // to Upcoming beneath it was allowed. The rung is retired and such an auction
  // now derives `active`, so the exemption is restated explicitly rather than
  // inherited from the phase.
  if (parentAuctionAwaitingClose) return null;
  // `paused` counts as live here: §7a's signed decision keeps the lot-status
  // CEILING treating a paused auction like an active one (pause halts bidding,
  // not cataloguing rules), and this is that ceiling.
  //
  // BL-1291 closes §3.2's named gap: `accepting-pre-bids` EXISTS as an
  // `AuctionPhaseKind`, so the rule can be stated. It belongs here for the same
  // reason `active` does — buyers are already placing standing max bids under a
  // pre-bidding auction, so dropping a lot back to `ready` beneath them would
  // strand it un-biddable inside a sale that is already taking bids.
  return parentPhase === 'active' || parentPhase === 'paused' || parentPhase === 'accepting-pre-bids'
    ? 'not-openable'
    : null;
}

/**
 * The parent-auction precondition on a `restore` landing (BUG-1460, matrix
 * §3.10) — invariant I2 AND the landing-target rule, composed.
 *
 * The two rules COMPOSE rather than merge: they answer different questions on
 * disjoint targets. I2 governs `ready`; {@link parentAuctionBlockForLandingTarget}
 * governs the two biddable rungs (a lot may not be landed where the parent
 * cannot make it derive). Neither is re-stated here — this function only picks
 * which one applies.
 *
 * BUG-1685 lifted it out of `stamp-set-transitions.ts` alongside its I2 half, so
 * the batch check's `restore` entry and the write-side effect share it. While
 * `restore` had no route the write path was its only caller and the duplication
 * was invisible; giving the transition a UI is exactly what makes a second
 * reader appear.
 *
 * @param input.parentPhase The parent auction's ADR-023 derived phase kind.
 * @param input.target The landing the operator picked. `'deleted'` is accepted
 *   (the write-side target type includes it) and answers `null` under any
 *   non-deleted parent — a restore never lands a lot back in the bin, so it
 *   never reaches the shared rule, which is keyed on `LotPhaseKind`. Under a
 *   deleted parent the BUG-1728 branch answers first, for every target.
 * @param input.auctionGoLiveBlockerCount How many go-live requirements the parent
 *   currently fails (`getAuctionReadyBlockers(...).length`).
 * @param input.parentAuctionAwaitingClose Whether the parent's sale has finished
 *   but is not yet stamped closed.
 * @param input.parentAuctionHasOpened Whether the parent's `opened_at` is set
 *   (BL-2278) — forwarded to {@link parentAuctionBlockForLandingTarget}.
 * @param input.parentAuctionCloseStage The parent's derived post-close stage
 *   (BL-2280) — forwarded to {@link parentAuctionBlockForLandingTarget}.
 * @returns `null` when the landing is reachable, otherwise the blocking reason —
 *   `'auction-deleted'` for any landing under a soft-deleted parent (BUG-1728).
 * @sideEffect none — pure.
 */
export function parentAuctionBlockForRestoreTarget(input: {
  parentPhase: AuctionPhaseKind;
  target: LotPhaseKind | 'deleted';
  auctionGoLiveBlockerCount: number;
  parentAuctionAwaitingClose: boolean;
  parentAuctionHasOpened: boolean;
  parentAuctionCloseStage: number;
}): BatchBlockReason | null {
  const {
    parentPhase,
    target,
    auctionGoLiveBlockerCount,
    parentAuctionAwaitingClose,
    parentAuctionHasOpened,
    parentAuctionCloseStage,
  } = input;
  // BUG-1728 — first, and for EVERY landing: a deleted parent cannot hold a live
  // lot (ADR 018 — Soft Delete; the auction's delete is refused while one
  // exists). The two rules below cannot see this — `draft` and `ready` read no
  // auction column, so they answered null and the lot came back under an
  // auction no surface can reach.
  if (parentPhase === 'deleted') return 'auction-deleted';
  const i2 = parentAuctionBlockForReadyLanding({
    parentPhase,
    target,
    parentAuctionAwaitingClose,
  });
  if (i2) return i2;
  if (target === 'deleted') return null;
  return parentAuctionBlockForLandingTarget({
    parentPhase,
    target,
    auctionGoLiveBlockerCount,
    parentAuctionAwaitingClose,
    parentAuctionHasOpened,
    parentAuctionCloseStage,
  });
}
